/**
 * AgentContext — global state for the user's manually-selected agent.
 *
 * This is the source of truth for the agent chosen via the UserPopover
 * "Switch Agent" control. The shared ChatPanel on the main app, IVT and Event
 * Hub all use it (App.tsx's ChatShell, utils/chatAgent.ts).
 *
 * Selection priority on first load:
 *   1. localStorage (the user's last manual choice — per browser, persisted)
 *   2. job-role default — the agents the user is authorized to invoke
 *      (Permission Manager), with Marketing users preferring the Marketing
 *      agent and everyone else defaulting to the Sales agent (rocky). See
 *      resolveDefaultAgentForUser / rankAuthorizedAgents below.
 *   3. DEFAULT_AGENT ("rocky") as the final fallback
 *
 * Two planes, kept separate on purpose:
 *   - Authorization (listAuthorizedResources) is the HARD boundary — a user is
 *     only ever defaulted to an agent they may actually invoke, so the default
 *     never lands on an agent the Gateway would then 403.
 *   - Job role is the SOFT preference — it only orders the agents the user is
 *     already authorized for; it never grants or withholds access.
 *
 * Agent restriction (LDAP group, client-side UX):
 *   HIDDEN_AGENT_IDS (Campaign Copilot) are allowed to no one. The Marketing Agent needs MARKETING_AGENT_LDAP_GROUP (or rocky-admin); a user
 *   admitted only through that group gets nothing else. See isAgentAllowedFor.
 *   The picker lists only allowed agents, the job-role default ranks only
 *   allowed agents, and any other agent — a stored choice, a ?agent= override —
 *   is clamped to the first allowed one. The Gateway's AgentAuthorizer remains
 *   the authoritative invoke check.
 *
 * Agent mapping:
 *   UI label          → runtime agent_name
 *   "Sales Agent"     → "rocky"
 *   "Marketing Agent" → "marketing_agent"
 */

import i18n from '../i18n';
import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { permissionManagerClient } from '../client/permissionManagerClient';
import { ADMIN_PAGE_LDAP_GROUP, HIDDEN_AGENT_IDS, MARKETING_AGENT_IDS, MARKETING_AGENT_LDAP_GROUP, ROCKY_USER_LDAP_GROUP } from '../config';
import { useAuth } from './AuthContext';
import { useUserProfile } from './UserProfileContext';
import type { PapiJob } from '../client/GatewayClient';

// ── Agent registry ──────────────────────────────────────────────────────────

/** Agents with a welcome message in chat:welcome. */
export type WelcomeKey = 'rocky' | 'marketing_agent' | 'eval';

export interface AgentInfo {
  id: string;         // runtime agent_name (sent to backend)
  label: string;      // UI display name
  shortLabel: string; // badge text
  welcomeKey: WelcomeKey; // chat:welcome.<key>, the first message of a fresh session
}

export const AGENT_REGISTRY: AgentInfo[] = [
  {
    id: 'rocky',
    label: 'Sales Agent',
    shortLabel: 'SALES',
    welcomeKey: 'rocky',
  },
  {
    id: 'marketing_agent',
    label: 'Marketing Agent',
    shortLabel: 'MKT',
    welcomeKey: 'marketing_agent',
  },
];

export const DEFAULT_AGENT = AGENT_REGISTRY[0];

/**
 * Agents pinned to ONE surface (e.g. the Evaluation pages). They are chat
 * targets there, but are deliberately NOT in AGENT_REGISTRY: never in the
 * "Switch Agent" picker, never restored from localStorage, never a job-role
 * default. They only need an entry here so getAgentInfo / getAgentWelcome
 * resolve their own label and welcome instead of falling back to Rocky.
 */
export const EMBEDDED_AGENTS: AgentInfo[] = [
  {
    id: 'eval',
    label: 'Evaluation Agent',
    shortLabel: 'EVAL',
    welcomeKey: 'eval',
  },
];

/** Runtime agent_name of the chat pinned to the /eval pages. */
export const EVAL_CHAT_AGENT_ID = 'eval';

/**
 * Agents that can have their access MANAGED (Access Control page) but are NOT
 * user-selectable runtime agents — they are deliberately kept out of
 * AGENT_REGISTRY so they never appear in the "Switch Agent" picker, never
 * become a job-role default, and are never a chat target.
 *
 * This is purely an admin convenience: the Access Control dropdown already
 * auto-includes any agent the caller holds `manage` on (ListAuthorizedResources),
 * so an entry here only gives that agent a friendly label and makes it
 * selectable before its first grant exists. Keep it `{ id, label }` only —
 * shortLabel/welcome are Switch-Agent / ChatPanel concerns that do not apply.
 */
export interface ManageableAgent {
  id: string;    // resourceId managed in Access Control
  label: string; // UI display name in the Access Control agent dropdown
}

export const MANAGEABLE_AGENTS: ManageableAgent[] = [
  { id: 'acquisition_agent', label: 'Acquisition Agent' },
  // Chat target on /eval only (EMBEDDED_AGENTS); listed so admins can grant invoke.
  { id: 'eval', label: 'Evaluation Agent' },
  // Hidden from every user (HIDDEN_AGENT_IDS) but its runtime is still live, so
  // admins keep managing its grants while it is merged into marketing_agent.
  { id: 'campaign_copilot_agent', label: 'Campaign Copilot' },
];

export function getAgentInfo(agentId: string): AgentInfo {
  return AGENT_REGISTRY.find(a => a.id === agentId)
    ?? EMBEDDED_AGENTS.find(a => a.id === agentId)
    ?? DEFAULT_AGENT;
}

/**
 * Welcome message for a fresh chat session with the given agent, in the UI locale.
 * Components pass their `t` (chat namespace) so it re-renders on a locale change;
 * without one it uses the current locale at call time.
 */
export function getAgentWelcome(agentId: string, t: (key: `welcome.${WelcomeKey}`) => string = i18n.getFixedT(null, 'chat')): string {
  return t(`welcome.${getAgentInfo(agentId).welcomeKey}`);
}

const STORAGE_KEY = 'rocky_selected_agent';

function isKnownAgent(id: string | null | undefined): id is string {
  return !!id && AGENT_REGISTRY.some(a => a.id === id);
}

function readStoredAgent(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isKnownAgent(v) ? v : null;
  } catch {
    return null;
  }
}

function writeStoredAgent(agentId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, agentId);
  } catch {
    /* ignore — localStorage unavailable (private mode / quota) */
  }
}

/**
 * Marketing job-family detection → preferred agent order.
 *
 * Rocky's agents are effectively two groups: Marketing (marketing_agent) and everything-else-defaults-to-Sales (rocky). So the
 * ranking only needs to answer ONE question: is this user a Marketing person?
 * If yes, prefer the marketing agents; otherwise fall through to registry order,
 * which puts rocky (the Sales agent) first.
 *
 * We deliberately do NOT enumerate Sales job families. Amazon's job-family code
 * space is large and DB-maintained (SALES, SALACT, SLSOPS, SALTEC, BUSDEV,
 * SOLARC, TAM, CSMT, PROSRV, TIS, ...), and GCR sales roles land on many of them
 * (e.g. a GCR Business Developer is BUSDEV, not SALES). Enumerating them would
 * silently miss codes and misroute users. Marketing is the only distinction that
 * must be positively identified; the long tail correctly defaults to rocky.
 *
 * PAPI's `job.jobFamilyName` carries the job-family CODE (e.g. "MKTG"), not the
 * full word ("Marketing"), so we match both. Matched case-insensitively as a
 * substring against the job family + business title. Only used to ORDER agents
 * the user is already authorized to invoke — never to grant or withhold access.
 */
const MARKETING_JOB_KEYWORDS: readonly string[] = ['marketing', 'mktg'];
const MARKETING_AGENT_ORDER: readonly string[] = ['marketing_agent', 'rocky'];

/**
 * The lowercased text matched against the marketing keywords. Combines the job
 * fields that carry the role so a match does not depend on which one PAPI
 * populates or how it is spelled: `jobFamilyName` ("Marketing" / "MKTG") and
 * `businessTitle` ("Sr.Mgr, General MKTG", "Field Marketing Lead").
 */
function jobSignal(job: PapiJob | null | undefined): string {
  return [job?.jobFamilyName, job?.businessTitle]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Rank the agents a user is authorized to invoke, returning the preferred
 * default first. Pure (no I/O) so it is unit-testable.
 *
 * - `authorizedIds` is the set the authorization plane already approved; this
 *   function only REORDERS it and never adds an id that is not in it.
 * - A Marketing user (job family / title matches MARKETING_JOB_KEYWORDS) prefers
 *   the marketing agents in MARKETING_AGENT_ORDER; every other user — Sales and
 *   the long tail of other job families alike — falls through to registry order,
 *   which puts rocky (the Sales agent) first.
 * - Within each group, ties fall back to registry order so the result is
 *   deterministic.
 * - With no authorized agents, returns null and the caller falls back to
 *   DEFAULT_AGENT.
 */
export function rankAuthorizedAgents(
  authorizedIds: readonly string[],
  job: PapiJob | null | undefined,
): string | null {
  const authorized = authorizedIds.filter(isKnownAgent);
  if (authorized.length === 0) return null;

  const signal = jobSignal(job);
  const isMarketing = MARKETING_JOB_KEYWORDS.some(k => signal.includes(k));
  const preferredOrder = isMarketing ? MARKETING_AGENT_ORDER : [];

  const registryOrder = (id: string) => {
    const i = AGENT_REGISTRY.findIndex(a => a.id === id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const rankOf = (id: string) => {
    const pref = preferredOrder.indexOf(id);
    // Preferred agents sort ahead of the rest; ties and non-preferred agents
    // fall back to registry order for a stable, deterministic result.
    return pref === -1 ? [1, registryOrder(id)] : [0, pref];
  };

  const sorted = [...authorized].sort((a, b) => {
    const [ga, ra] = rankOf(a);
    const [gb, rb] = rankOf(b);
    return ga - gb || ra - rb;
  });
  return sorted[0];
}

/**
 * Resolve the default agent for the signed-in user: the agents they may invoke
 * (Permission Manager, the hard authorization boundary), ranked by their job
 * family (the soft preference). Returns DEFAULT_AGENT when the user has no
 * authorized agents, when authorization cannot be read, or when nothing matches
 * — behaviour is never worse than the prior "always rocky" placeholder.
 */
async function resolveDefaultAgentForUser(
  job: PapiJob | null | undefined,
  isAllowed: (agentId: string) => boolean,
): Promise<string> {
  try {
    const { resources } = await permissionManagerClient.listAuthorizedResources({
      resourceType: 'agent',
      action: 'invoke',
    });
    // Rank only agents the LDAP plane also allows, so a Marketing-family rocky-user
    // lands on their next marketing preference rather than on a hidden agent.
    const ranked = rankAuthorizedAgents(resources.map(r => r.resourceId).filter(isAllowed), job);
    return ranked ?? DEFAULT_AGENT.id;
  } catch {
    // Authorization unreadable (expired session already retried in the client,
    // network, 5xx): fall back rather than block the UI on a default choice.
    return DEFAULT_AGENT.id;
  }
}

/**
 * Whether a user in `ldapGroups` may chat with `agentId`. Pure so it is unit-testable.
 *
 * - HIDDEN_AGENT_IDS: no one, rocky-admin included.
 * - rocky-admin: every other agent.
 * - MARKETING_AGENT_IDS: only with MARKETING_AGENT_LDAP_GROUP (rocky-user alone is not enough).
 * - Every other agent — including unregistered ids, e.g. a ?agent= test target — is
 *   allowed unless the user's only main-app group is the Marketing Agent group. Users
 *   with no main-app group at all (ivthub-only, whose chat runs on /ivt) keep them.
 */
export function isAgentAllowedFor(ldapGroups: readonly string[], agentId: string): boolean {
  if (HIDDEN_AGENT_IDS.includes(agentId)) return false;
  if (ldapGroups.includes(ADMIN_PAGE_LDAP_GROUP)) return true;
  const inMarketingGroup = ldapGroups.includes(MARKETING_AGENT_LDAP_GROUP);
  if (MARKETING_AGENT_IDS.includes(agentId)) return inMarketingGroup;
  return !inMarketingGroup || ldapGroups.includes(ROCKY_USER_LDAP_GROUP);
}

// ── Context ─────────────────────────────────────────────────────────────────

interface AgentContextValue {
  /** Current runtime agent_name (e.g. 'rocky', 'marketing_agent') */
  currentAgent: string;
  /** Info object for the current agent */
  currentAgentInfo: AgentInfo;
  /** Switch to a different agent (persisted to localStorage). Ignores agents the user may not select. */
  switchAgent: (agentId: string) => void;
  /** Switch Agent picker options: AGENT_REGISTRY narrowed to the user's allowed agents. */
  selectableAgents: AgentInfo[];
  /** Whether the user may chat with `agentId` (see isAgentAllowedFor). */
  isAgentAllowed: (agentId: string) => boolean;
}

const AgentContext = createContext<AgentContextValue>({
  currentAgent: DEFAULT_AGENT.id,
  currentAgentInfo: DEFAULT_AGENT,
  switchAgent: () => {},
  selectableAgents: AGENT_REGISTRY,
  isAgentAllowed: () => true,
});

// ── Provider ────────────────────────────────────────────────────────────────

export function AgentProvider({ children }: { children: ReactNode }) {
  const { profile, isLoading } = useUserProfile();
  const { ldap_groups } = useAuth();
  // AuthContext rebuilds ldap_groups on every idToken change (silent refresh included),
  // so key on its contents: a new isAgentAllowed would rerun the job-role default
  // effect below — another Permission Manager call and a possible mid-session switch.
  const groupsKey = ldap_groups.join('\n');
  const isAgentAllowed = useCallback(
    (id: string) => isAgentAllowedFor(groupsKey ? groupsKey.split('\n') : [], id),
    [groupsKey],
  );

  // Initialize synchronously from localStorage (the user's last choice) so the
  // first render already reflects their preference; only when no stored choice
  // exists do we fall back to the job-role default below.
  const [currentAgent, setCurrentAgent] = useState<string>(() => readStoredAgent() ?? DEFAULT_AGENT.id);

  // If the user has never manually chosen an agent, resolve the job-role default
  // once their profile has loaded (the ranking reads the PAPI job family). We
  // wait for the profile so a Sales/Marketing user is not briefly defaulted to
  // rocky before their role is known; a profile that fails to load resolves to
  // null here and the resolver falls back to DEFAULT_AGENT either way.
  useEffect(() => {
    if (readStoredAgent()) return; // user has an explicit choice — respect it
    if (isLoading) return;         // wait for the profile before ranking by role
    let cancelled = false;
    resolveDefaultAgentForUser(profile?.job, isAgentAllowed)
      .then(id => {
        if (!cancelled && isKnownAgent(id)) setCurrentAgent(id);
      })
      .catch(() => { /* keep DEFAULT_AGENT fallback */ });
    return () => { cancelled = true; };
  }, [profile, isLoading, isAgentAllowed]);

  const switchAgent = useCallback((agentId: string) => {
    if (!isKnownAgent(agentId) || !isAgentAllowed(agentId)) return;
    setCurrentAgent(prev => (prev === agentId ? prev : agentId));
    writeStoredAgent(agentId);
  }, [isAgentAllowed]);

  // Clamp at read time rather than rewriting state: ldap_groups arrives after the
  // first render (and can change on re-login), and a disallowed stored or default
  // choice must not leak through in between. The stored choice is left untouched,
  // so the same browser keeps it for an account that is allowed it.
  const selectableAgents = useMemo(() => AGENT_REGISTRY.filter(a => isAgentAllowed(a.id)), [isAgentAllowed]);
  const effectiveAgent = isAgentAllowed(currentAgent) ? currentAgent : (selectableAgents[0] ?? DEFAULT_AGENT).id;
  const currentAgentInfo = getAgentInfo(effectiveAgent);

  return (
    <AgentContext.Provider value={{ currentAgent: effectiveAgent, currentAgentInfo, switchAgent, selectableAgents, isAgentAllowed }}>
      {children}
    </AgentContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useAgent() {
  return useContext(AgentContext);
}
